import type { FunctionComponent, SVGProps } from "react"
import type { DailyHoroscopeCategoryKey, DailyHoroscopeData } from "../types"

import HealthIcon from "@/assets/img/today/horoscope/health.svg?react"
import CareerIcon from "@/assets/img/today/horoscope/career.svg?react"
import EmotionsIcon from "@/assets/img/today/horoscope/emotions.svg?react"
import TravelIcon from "@/assets/img/today/horoscope/travel.svg?react"

type SvgComponent = FunctionComponent<SVGProps<SVGSVGElement>>

export interface FeatureCard {
  title: string
  description: string
  tags: string[]
}

/** Focus / Troubles cards. Static copy (the source left these as placeholders). */
export const HOROSCOPE_FEATURES: { focus: FeatureCard; troubles: FeatureCard } = {
  focus: {
    title: "Focus",
    description: "The energies that support you most during this period",
    tags: ["Communication", "New connections", "Creativity", "Quick wins", "Curiosity", "Movement"],
  },
  troubles: {
    title: "Troubles",
    description: "The challenges that may create tension or slow your progress",
    tags: ["Overthinking", "Scattered focus", "Impatience", "Mixed signals", "Restlessness"],
  },
}

export interface HoroscopePrediction {
  key: DailyHoroscopeCategoryKey
  title: string
  Icon: SvgComponent
  percentage: number
  upTag: string
  downTag: string
  description: string
}

const PREDICTION_ICON_BY_KEY: Record<DailyHoroscopeCategoryKey, SvgComponent> = {
  health: HealthIcon,
  career: CareerIcon,
  emotions: EmotionsIcon,
  travel: TravelIcon,
}

const PREDICTION_ORDER: readonly DailyHoroscopeCategoryKey[] = [
  "health",
  "career",
  "emotions",
  "travel",
]

function toNonEmptyText(value: string | undefined): string | null {
  if (!value) return null
  const normalized = value.trim()
  return normalized.length > 0 ? normalized : null
}

function toScore(value: number | undefined): number | null {
  if (typeof value !== "number" || Number.isNaN(value)) return null
  return Math.min(100, Math.max(0, Math.round(value)))
}

/**
 * Builds prediction cards strictly from `daily_horoscope` payload.
 * Returns null when required category fields are missing.
 */
export function buildHoroscopePredictions(
  dailyHoroscope: DailyHoroscopeData | null | undefined,
): HoroscopePrediction[] | null {
  if (!dailyHoroscope) return null

  const backendByKey = new Map(
    (dailyHoroscope?.categories ?? []).map((category) => [category.key, category]),
  )

  const predictions: HoroscopePrediction[] = []

  for (const key of PREDICTION_ORDER) {
    const category = backendByKey.get(key)
    const title = toNonEmptyText(category?.label)
    const upTag = toNonEmptyText(category?.pro)
    const downTag = toNonEmptyText(category?.con)
    const description = toNonEmptyText(category?.body)
    const percentage = toScore(category?.score)

    if (!title || !upTag || !downTag || !description || percentage === null) {
      return null
    }

    predictions.push({
      key,
      title,
      Icon: PREDICTION_ICON_BY_KEY[key],
      percentage,
      upTag,
      downTag,
      description,
    })
  }

  return predictions
}

export const QUIET_SIGNS = {
  label: "Luck Summary",
  title: "Quiet Signs of Luck",
  description:
    "Luck today is not loud or dramatic. It may appear through small openings, helpful timing, short conversations, or delayed answers that turn out to be useful. Pay attention to subtle signs instead of waiting for one big breakthrough.",
}

export const HOROSCOPE_COUNTDOWN = {
  title: "A Sign, Not a Sentence",
  description:
    "The stars can reveal a direction, but they do not decide your future. Your actions still lead the way.",
}
